import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';
import { ScaleDefinition } from '../utils/multiScaleAnalysis';

interface CustomScaleManagerProps {
  customScales: Record<string, ScaleDefinition>;
  onAddScale: (scaleId: string, definition: Omit<ScaleDefinition, 'type'>) => void;
  onUpdateScale: (scaleId: string, definition: Omit<ScaleDefinition, 'type'>) => void;
  onRemoveScale: (scaleId: string) => void;
}

interface ScaleFormData {
  name: string;
  values: Array<{ text: string; value: number }>;
}

export function CustomScaleManager({ 
  customScales, 
  onAddScale, 
  onUpdateScale, 
  onRemoveScale 
}: CustomScaleManagerProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ScaleFormData>({
    name: '',
    values: [
      { text: '', value: 5 },
      { text: '', value: 4 },
      { text: '', value: 3 },
      { text: '', value: 2 },
      { text: '', value: 1 }
    ]
  });

  const resetForm = () => {
    setFormData({
      name: '',
      values: [
        { text: '', value: 5 },
        { text: '', value: 4 },
        { text: '', value: 3 },
        { text: '', value: 2 },
        { text: '', value: 1 }
      ]
    });
  };

  const loadScaleToForm = (scale: ScaleDefinition) => {
    setFormData({
      name: scale.name,
      values: Object.entries(scale.values)
        .sort(([, a], [, b]) => b - a)
        .map(([text, value]) => ({ text, value }))
    });
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('يرجى إدخال اسم المقياس');
      return;
    }

    const hasEmptyValues = formData.values.some(v => !v.text.trim());
    if (hasEmptyValues) {
      alert('يرجى إدخال جميع قيم المقياس');
      return;
    }

    const values: Record<string, number> = {};
    formData.values.forEach(v => {
      values[v.text.trim()] = v.value;
    });

    const minValue = Math.min(...formData.values.map(v => v.value));
    const maxValue = Math.max(...formData.values.map(v => v.value));

    const definition: Omit<ScaleDefinition, 'type'> = {
      name: formData.name.trim(),
      values,
      normalizedRange: [minValue, maxValue]
    };

    if (editingId) {
      onUpdateScale(editingId, definition);
      setEditingId(null);
    } else {
      const scaleId = `custom_${Date.now()}`;
      onAddScale(scaleId, definition);
      setIsAdding(false);
    }

    resetForm();
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    resetForm();
  };

  const handleEdit = (scaleId: string, scale: ScaleDefinition) => {
    setEditingId(scaleId);
    loadScaleToForm(scale);
  };

  const handleRemove = (scaleId: string, scaleName: string) => {
    if (confirm(`هل أنت متأكد من حذف المقياس "${scaleName}"؟`)) {
      onRemoveScale(scaleId);
    }
  };

  const updateValue = (index: number, field: 'text' | 'value', newValue: string | number) => {
    const newValues = [...formData.values];
    newValues[index] = { ...newValues[index], [field]: newValue };
    setFormData({ ...formData, values: newValues });
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800">إدارة المقاييس المخصصة</h3>
        {!isAdding && !editingId && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            إضافة مقياس جديد
          </button>
        )}
      </div>

      {/* نموذج إضافة/تحرير المقياس */}
      {(isAdding || editingId) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-bold text-gray-800 mb-4">
            {editingId ? 'تحرير المقياس' : 'إضافة مقياس جديد'}
          </h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اسم المقياس
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="مثال: مقياس سهولة الاستخدام"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                قيم المقياس (من الأعلى للأقل)
              </label>
              <div className="space-y-2">
                {formData.values.map((item, index) => (
                  <div key={index} className="flex gap-3 items-center">
                    <input
                      type="number"
                      value={item.value}
                      onChange={(e) => updateValue(index, 'value', parseInt(e.target.value) || 1)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                      min="1"
                      max="10"
                    />
                    <input
                      type="text"
                      value={item.text}
                      onChange={(e) => updateValue(index, 'text', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`النص للدرجة ${item.value}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingId ? 'حفظ التعديلات' : 'إضافة المقياس'}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* قائمة المقاييس المخصصة */}
      <div className="space-y-4">
        {Object.entries(customScales).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>لا توجد مقاييس مخصصة حتى الآن</p>
            <p className="text-sm">اضغط "إضافة مقياس جديد" لإنشاء مقياس مخصص</p>
          </div>
        ) : (
          Object.entries(customScales).map(([scaleId, scale]) => (
            <div key={scaleId} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800 mb-2">{scale.name}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
                    {Object.entries(scale.values)
                      .sort(([, a], [, b]) => b - a)
                      .map(([text, value]) => (
                        <div key={text} className="bg-gray-50 px-3 py-1 rounded text-center">
                          <div className="font-medium text-blue-600">{value}</div>
                          <div className="text-gray-700 text-xs">{text}</div>
                        </div>
                      ))}
                  </div>
                </div>
                <div className="flex gap-2 mr-4">
                  <button
                    onClick={() => handleEdit(scaleId, scale)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="تحرير"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleRemove(scaleId, scale.name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="حذف"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 